/* eslint-disable quote-props */

import {onFCP, onCLS, onFID, onLCP, onTTFB, Metric} from 'web-vitals';

import {createLogger} from '~/shared/logging';
import {getCurrentLocation} from '~/shared/router';
import {
  selectFilteredRestaurants,
  selectActiveFilters,
  selectCurrentDeliveryMethod,
  selectFilters,
  selectCurrentAddress,
  selectUserData,
} from '~/shared/store/selectors';
import store from '~/shared/store';
import {Cuisine, OrderData, User} from '~/shared/store/models';
import {RestaurantsGroupTypes, STORE_BY_BUSINESS_TYPE} from '~/shared/consts/restaurantConsts';
import {getRestaurantOpenStatusForBI} from '~/shared/utils/restaurants/restaurantStatus';
import {settingsOptionAnalyticsNameMap} from '~/shared/services/analytics/utils';

import {
  AnalyticsDataProps,
  CurrentProduct,
  EventClickedSearchResultProps,
  EventDataType,
  EventLoginProps,
  EventSelectResProps,
  EventDeleteAccountProps,
  FilterTypeKey,
  filterTypesMapForAnalyticsNoPrivacy,
  TrackPageviewProps,
  EventHasClickedOfficeSupplyFilterProps,
  ActiveOrderRecivedProps,
  ActiveOrderOnClickProps,
  TenBisCreditEventData,
  TenBisCreditEventTypes,
  FormErrorMessageProps,
  ViewedCarouselProps,
  HasClickedShowAllCarouselProps,
  GeneralErrorModalEventNames,
  GeneralErrorModalClickOptions,
  OnlyOpenForModalEventNames,
  OnlyOpenForModalClickOptions,
  GoogleAnalyticsEvents,
  NlxOrderTypeSelectProps,
  OtlRuleLimitationEventData,
  ThreeDsAnalyticsPayload,
  LoginRegisterProps,
  TrackerSharedAnalyticsProps,
  AutoCreditToggleProps,
  AutoCreditToggleFailedProps,
  SettingsOptionChangeProps,
  AddressStartSearchProps,
  ModifiedAddressProps,
} from './analyticsModels';
import {trackGtmEvent} from './trackGtmEvent';

const logger = createLogger('analytics');

// "unspecified" is used when we can't log data due to privacy protection issues
const FILTER_VALUE_UNSPECIFIED = 'unspecified';

// use this if you want to portray what the user has chosen.
// some fields will be "unspecified" due to privacy protection issues
const filterTypesMapForAnalyticsWithPrivacy = {
  ...filterTypesMapForAnalyticsNoPrivacy,
  isGlutenFree: FILTER_VALUE_UNSPECIFIED,
  isKosher: FILTER_VALUE_UNSPECIFIED,
  isNotKosher: FILTER_VALUE_UNSPECIFIED,
};

export const isFilterExists = ({analyticsValues}: {analyticsValues: Record<string, any>}) => {
  return Object.values(analyticsValues || {}).some(filter => filter && filter !== 'unspecified');
};

const commonAnalyticsData: AnalyticsDataProps = {
  userData: null,
  userType: null,
  currentAddressCityName: null,
};

export function getUserTypeFromUserData(userData: User) {
  return userData.isCompanyUser ? 'b2b' : 'b2c';
}

export function getLoggedInType(userDetails: User) {
  return `login_${getUserTypeFromUserData(userDetails)}`;
}

const getCurrentProductsForGA = (data: CurrentProduct[]) => data.map(({
  productID,
  productName,
  productPrice,
  productCategory,
  productQuantity,
  productVariant,
}) => ({
  item_id: productID,
  item_name: productName,
  item_variant: productVariant,
  item_category: productCategory,
  price: productPrice,
  quantity: productQuantity,
}));

export const convertFilterForAnalytics = (activeFilters: ReturnType<typeof selectFilters>) => {
  const {lastFilterTypeSelected} = activeFilters;
  const activeCuisineId =
    activeFilters.cuisines &&
    Object.keys(activeFilters.cuisines).find(cuisineId => !!activeFilters.cuisines?.[cuisineId]);

  const lastFilterTypeSelectedKey =
    filterTypesMapForAnalyticsNoPrivacy[lastFilterTypeSelected as FilterTypeKey] || lastFilterTypeSelected;

  const analyticsValues = {
    kitchenType: activeCuisineId || 0,
    deliveryCost: Number(activeFilters.freeDelivery || 0),
    freeDeliveryWithTracking: Number(activeFilters.isScoober || 0),
    new: Number(activeFilters.newRestaurants || 0),
    discount: Number(activeFilters.discountCoupon || 0),
    glutenFree: FILTER_VALUE_UNSPECIFIED, // for glutenFree & kosher, always use unspecified due to privacy protection issues
    vegan: Number(activeFilters.isVegan || 0),
    filterEcoFriendly: Number(activeFilters.isEnvironmentFriendly || 0),
    isKosher: FILTER_VALUE_UNSPECIFIED, // for glutenFree & kosher, always use unspecified due to privacy protection issues
    isNotKosher: FILTER_VALUE_UNSPECIFIED, // for glutenFree & kosher, always use unspecified due to privacy protection issues
  };

  const filterAsString = `
    kitchenType=${analyticsValues.kitchenType}&
    deliveryCost=${analyticsValues.deliveryCost}&
    freeDeliveryWithTracking=${analyticsValues.freeDeliveryWithTracking}&
    new=${analyticsValues.new}&
    discount=${analyticsValues.discount}&
    glutenFree=${analyticsValues.glutenFree}& 
    vegan=${analyticsValues.vegan}&
    filterEcoFriendly=${analyticsValues.filterEcoFriendly}&
    isNotKosher=${analyticsValues.isNotKosher}&
    isKosher=${analyticsValues.isKosher}`.replace(/\s+/g, '');

  return {
    lastFilterTypeSelectedKey,
    analyticsValues,
    filterAsString,
  };
};

const eventHandlers = {
  register({userData, analyticsDataProps}: {userData: User; analyticsDataProps: LoginRegisterProps}) {
    const userType = getUserTypeFromUserData(userData);
    const LoggedInType = getLoggedInType(userData);

    Object.assign(commonAnalyticsData, {
      userData,
      userType,
    });

    trackGtmEvent({
      name: 'hasSuccessfullyRegistered',
      eventData: {
        userType,
        onCheckout: analyticsDataProps?.onCheckout ? 1 : 0,
        authentication_provider: analyticsDataProps?.authenticationProvider,
      },
      pageData: {
        LoggedInType,
      },
      commonAnalyticsData,
    });
  },

  login({userData, isAutomaticLogin, currentAddressCityName, onCheckout, authenticationProvider}: EventLoginProps) {
    Object.assign(commonAnalyticsData, {
      userData,
      userType: getUserTypeFromUserData(userData),
      currentAddressCityName,
    });

    if (!isAutomaticLogin) {
      const LoggedInType = getLoggedInType(userData);
      trackGtmEvent({
        name: 'hasSuccessfullyLoggedIn',
        eventData: {
          onCheckout: onCheckout ? 1 : 0,
          authentication_provider: authenticationProvider,
        },
        pageData: {
          LoggedInType,
        },
        commonAnalyticsData,
      });
    }
  },

  headerLogin_click() {
    trackGtmEvent({
      name: 'headerLogin_click',
      commonAnalyticsData,
    });
  },

  partnerWithUs_click() {
    trackGtmEvent({
      name: 'partnerWithUs_click',
      commonAnalyticsData,
    });
  },

  becomeCourier_click() {
    trackGtmEvent({
      name: 'becomeCourier_click',
      commonAnalyticsData,
    });
  },

  changeAddress({currentAddressCityName}: {currentAddressCityName: string}) {
    Object.assign(commonAnalyticsData, {
      currentAddressCityName,
    });
  },

  hasClickedPayWithPaypal() {
    trackGtmEvent({
      name: 'hasClickedPayWithPaypal',
      eventData: {
        screenType: 'Payment',
      },
      commonAnalyticsData,
    });
  },

  hasSelectedPaymentMethod() {
    trackGtmEvent({
      name: 'hasSelectedPaymentMethod',
      commonAnalyticsData,
    });
  },

  hasAddedPaymentOption() {
    trackGtmEvent({
      name: 'hasAddedPaymentOption',
      commonAnalyticsData,
    });
  },

  hasClickedAddPaymentOption() {
    trackGtmEvent({
      name: 'hasClickedAddPaymentOption',
      commonAnalyticsData,
    });
  },

  hasClickedAddCreditCard({linkType}: {linkType: string}) {
    trackGtmEvent({
      name: 'hasClickedAddCreditCard',
      eventData: {
        screenType: 'Checkout',
        linkType,
      },
      commonAnalyticsData,
    });
  },

  creditcardLimitationAdded(otlData: OtlRuleLimitationEventData) {
    trackGtmEvent({
      name: 'ccLimitation_added',
      eventData: otlData,
      commonAnalyticsData,
    });
  },

  hasClickedAddPaymentMethod() {
    trackGtmEvent({
      name: 'hasClickedAddPaymentMethod',
      commonAnalyticsData,
    });
  },

  hasApprovedCreditCard() {
    trackGtmEvent({
      name: 'hasApprovedCreditCard',
      commonAnalyticsData,
    });
  },

  hasSubmittedVoucher() {
    trackGtmEvent({
      name: 'hasSubmittedVoucher',
      commonAnalyticsData,
    });
  },

  loginForm_view({onCheckout}: Pick<LoginRegisterProps, 'onCheckout'>) {
    trackGtmEvent({
      name: 'loginForm_view',
      eventData: {
        onCheckout: onCheckout ? 1 : 0,
      },
    });
  },

  hasViewedRegistrationPopup({onCheckout}: Pick<LoginRegisterProps, 'onCheckout'>) {
    trackGtmEvent({
      name: 'hasViewedRegistrationPopup',
      eventData: {
        onCheckout: onCheckout ? 1 : 0,
      },
      commonAnalyticsData,
    });
  },

  loginForm_click({onCheckout, authenticationProvider}: LoginRegisterProps) {
    trackGtmEvent({
      name: 'loginForm_click',
      eventData: {
        onCheckout: onCheckout ? 1 : 0,
        authentication_provider: authenticationProvider,
      },
    });
  },

  registrationForm_click({onCheckout, authenticationProvider}: LoginRegisterProps) {
    trackGtmEvent({
      name: 'registrationForm_click',
      eventData: {
        onCheckout: onCheckout ? 1 : 0,
        authentication_provider: authenticationProvider,
      },
    });
  },

  registrationOTP_submit({onCheckout, authenticationProvider}: LoginRegisterProps) {
    trackGtmEvent({
      name: 'registrationOTP_submit',
      eventData: {
        onCheckout: onCheckout ? 1 : 0,
        authentication_provider: authenticationProvider,
      },
    });
  },

  loginOTP_submit({onCheckout, authenticationProvider}: LoginRegisterProps) {
    trackGtmEvent({
      name: 'loginOTP_submit',
      eventData: {
        onCheckout: onCheckout ? 1 : 0,
        authentication_provider: authenticationProvider,
      },
    });
  },

  hasSubmittedRegister({onCheckout, authenticationProvider}: LoginRegisterProps) {
    trackGtmEvent({
      name: 'hasSubmittedRegister',
      eventData: {
        onCheckout: onCheckout ? 1 : 0,
        authentication_provider: authenticationProvider,
      },
      commonAnalyticsData,
    });
  },

  hasSuccessfullyAddedCreditCard() {
    trackGtmEvent({
      name: 'hasSuccessfullyAddedCreditCard',
      eventData: {
        linkType: 'Payments Modal',
      },
      commonAnalyticsData,
    });
  },

  hasSubmittedOrder(eventData: EventDataType) {
    trackGtmEvent({
      name: 'hasSubmittedOrder',
      eventData,
      commonAnalyticsData,
    });
  },

  submitOrder_failed(eventData: EventDataType) {
    trackGtmEvent({
      name: 'submitOrder_failed',
      eventData,
    });
  },

  hasClickedIHaveAnAccount() {
    trackGtmEvent({
      name: 'hasClickedIHaveAnAccount',
      eventData: {
        linkType: 'register form',
      },
      commonAnalyticsData,
    });
  },

  hasClickedCreateAccount() {
    trackGtmEvent({
      name: 'hasClickedCreateAccount',
      eventData: {
        linkType: 'login form',
      },
      commonAnalyticsData,
    });
  },

  hasFormError(eventData: EventDataType) {
    trackGtmEvent({
      name: 'hasFormError',
      eventData,
      commonAnalyticsData,
    });
  },

  hasClickedPayWithPaypalOnCheckout(eventData: EventDataType) {
    trackGtmEvent({
      name: 'payWithPayPal_click',
      eventData,
      commonAnalyticsData,
    });
  },

  hasClickedAddressCheckoutDetails() {
    trackGtmEvent({
      name: 'addressDetailsCheckout_click',
      commonAnalyticsData,
    });
  },

  hasSortedRestaurants(sortOption: string) {
    trackGtmEvent({
      name: 'hasSortedRestaurants',
      eventData: {
        sortType: sortOption,
      },
      commonAnalyticsData,
    });
  },

  hasSelectedRestaurant({
    restaurantData: eventRestaurantData,
    secondOrderedByConfigUsed,
    groupId,
    itemIndexInGroup,
  }: EventSelectResProps) {
    const state = store.getState();
    const activeFilters = selectActiveFilters(state);
    const deliveryMethod = selectCurrentDeliveryMethod(state);
    const currentAddress = selectCurrentAddress(state);
    const userData = selectUserData(state);
    const isAnonymousWithoutAddress = !userData && !currentAddress;
    const isPreOrder = groupId === RestaurantsGroupTypes.CLOSED_WITH_FUTURE_ORDER_STARTING_TOMORROW;
    const isRestaurantPreOrder = Number(
      isPreOrder ||
      (eventRestaurantData.isOpenNow && eventRestaurantData.futureOrdersAvailable),
    );

    const {lastFilterTypeSelectedKey, filterAsString} = convertFilterForAnalytics(activeFilters);

    if (!eventRestaurantData) {
      logger.error('restaurant not found in hasSelectedRestaurant');
      return;
    }

    const {restaurantId: restaurantID, restaurantName, isScoober: isScooberRestaurant} = eventRestaurantData;

    const restaurantOpeningStatus = getRestaurantOpenStatusForBI({isAnonymousWithoutAddress, isPreOrder, isOpen: eventRestaurantData.isOpenNow, options: {
      tempClosedReason: eventRestaurantData.tempClosedReason,
    }});

    const eventData = {
      restaurantDeliveryTime: eventRestaurantData.deliveryTimeInMinutes, // expected delivery time in minutes
      restaurantFreeDelivery: eventRestaurantData.deliveryFee <= 0,
      restaurantMinimumPriceForOrder: eventRestaurantData.minimumPriceForOrder,
      restaurantNumOfReviews: eventRestaurantData.numOfReviews,
      restaurantOpeningStatus,
      restaurantRating: eventRestaurantData.reviewsRankDecimal,
      deliveryMethod,
      filterAll: filterAsString,
      lastFilterTypeSelectedKey,
      restaurantID,
      restaurantName,
      isScooberRestaurant,
      restaurantListOrderBy: secondOrderedByConfigUsed,
      isRestaurantPreOrder,
      cuisineId: eventRestaurantData.restaurantCuisineKeysList && eventRestaurantData.restaurantCuisineKeysList[0],
    };

    const restaurantData = {
      restaurantDeliveryTime: eventRestaurantData.deliveryTimeInMinutes, // expected delivery time in minutes
      restaurantRating: eventRestaurantData.reviewsRankDecimal,
      restaurantMinimumPriceForOrder: eventRestaurantData.minimumPriceForOrder,
      restaurantNumOfReviews: eventRestaurantData.numOfReviews,
      restaurantFreeDelivery: eventRestaurantData.deliveryFee <= 0,
      deliveryMethod,
      isScooberRestaurant,
      restaurantOpeningStatus,
    };

    trackGtmEvent({
      name: 'hasSelectedRestaurant',
      eventData,
      restaurantData,
      gaModifiedData: {
        ...eventData,
        resIndex: itemIndexInGroup + 1,
        groupId,
      },
      commonAnalyticsData,
    });
  },

  hasOrderedFromFilterResult({orderData}: {orderData: OrderData}) {
    const state = store.getState();
    const activeFilters = selectActiveFilters(state);

    const {lastFilterTypeSelectedKey, analyticsValues, filterAsString} = convertFilterForAnalytics(activeFilters);

    if (!isFilterExists({analyticsValues})) {
      return;
    }

    if (!lastFilterTypeSelectedKey) {
      return;
    }

    const transactionPaymentCost = orderData.shoppingCart.billingLines.find(
      line => line.type === 'TotalToCharge',
    )?.amount;

    const eventData = {
      lastFilterTypeSelectedKey,
      MadeTransactionFilterAll: filterAsString,
      transactionPaymentCost,
      restaurantId: orderData.restaurant.restaurantId,
      TransactionID: orderData.orderId,
    };

    // delete eventData.filterAsString;

    trackGtmEvent({
      name: 'hasOrderedFromFilterResult',
      eventData,
      commonAnalyticsData,
    });
  },

  dsChallenge_received({transactionAmount, businessType, errorMessage}: ThreeDsAnalyticsPayload) {
    trackGtmEvent({
      name: 'dsChallenge_received',
      eventData: {
        transactionAmount,
        businessType,
        error_message: errorMessage,
      },
    });
  },

  dsChallenge_failed({transactionAmount, businessType, errorMessage}: ThreeDsAnalyticsPayload) {
    trackGtmEvent({
      name: 'dsChallenge_failed',
      eventData: {
        transactionAmount,
        businessType,
        error_message: errorMessage,
      },
    });
  },

  dsChallenge_success({transactionAmount, businessType, errorMessage}: ThreeDsAnalyticsPayload) {
    trackGtmEvent({
      name: 'dsChallenge_success',
      eventData: {
        transactionAmount,
        businessType,
        error_message: errorMessage,
      },
    });
  },

  sendFilterDataLayer({filterType, isChecked}: {filterType: string; isChecked: boolean}) {
    if (!filterType || !store) {
      logger.warn('sendFilterDataLayer - filterType and store are required');
      return;
    }

    const state = store.getState();
    const filteredRestaurantsWithGroup = selectFilteredRestaurants(state);
    const filteredRestaurantsPresentedNumber = [...filteredRestaurantsWithGroup.filter(item => !item.isGroup)].length;
    const activeFilters = selectActiveFilters(state);

    if (isChecked) {
      const {lastFilterTypeSelectedKey, analyticsValues, filterAsString} = convertFilterForAnalytics(activeFilters);

      const dataLayerForFilteredRestaurants = {
        lastFilterTypeSelectedKey,
        countRestaurantShown: filteredRestaurantsPresentedNumber,
        filterKitchenType: analyticsValues.kitchenType,
        filterDeliveryCost: analyticsValues.deliveryCost,
        filterNew: analyticsValues.new,
        filterDiscount: analyticsValues.discount,
        filterKosher: analyticsValues.isKosher,
        filterVegan: analyticsValues.vegan,
        filterGlutenFree: analyticsValues.glutenFree,
        filterEcoFriendly: analyticsValues.filterEcoFriendly,
        filterFreeDeliveryWithTracking: analyticsValues.freeDeliveryWithTracking,
        filterAll: filterAsString,
      };

      trackGtmEvent({
        name: 'hasFilteredRestaurants',
        eventData: dataLayerForFilteredRestaurants,
        commonAnalyticsData,
      });
    } else {
      const dataLayerForUnfilteredRestaurants = {
        filterType: filterTypesMapForAnalyticsWithPrivacy[filterType as FilterTypeKey] || filterType,
        countRestaurantShown: filteredRestaurantsPresentedNumber,
      };
      trackGtmEvent({
        name: 'hasUnfilteredRestaurants',
        eventData: dataLayerForUnfilteredRestaurants,
        commonAnalyticsData,
      });
    }
  },

  hasViewedLoginStep(eventData: EventDataType) {
    trackGtmEvent({
      name: 'hasViewedLoginStep',
      eventData,
      commonAnalyticsData,
    });
  },

  hasClickedLogin(eventData: EventDataType) {
    trackGtmEvent({
      name: 'hasClickedLogin',
      eventData,
      commonAnalyticsData,
    });
  },

  hasClickedSendCodeAgain({linkType, onCheckout, authenticationProvider}: {linkType: string} & LoginRegisterProps) {
    trackGtmEvent({
      name: 'hasClickedSendCodeAgain',
      eventData: {
        linkType,
        onCheckout: onCheckout ? 1 : 0,
        authentication_provider: authenticationProvider,
      },
      commonAnalyticsData,
    });
  },

  hasClickedDidntReceiveCode(eventData: EventDataType) {
    trackGtmEvent({
      name: 'hasClickedDidntReceiveCode',
      eventData,
      commonAnalyticsData,
    });
  },

  hasClickedForgotPassword(eventData: EventDataType) {
    trackGtmEvent({
      name: 'hasClickedForgotPassword',
      eventData,
      commonAnalyticsData,
    });
  },

  hasChangedMarketingBannerManually() {
    trackGtmEvent({
      name: 'hasChangedMarketingBannerManually',
      commonAnalyticsData,
    });
  },

  hasClickedMarketingBanner(eventData: EventDataType) {
    trackGtmEvent({
      name: 'hasClickedMarketingBanner',
      eventData,
      commonAnalyticsData,
    });
  },

  hasStartedSearching() {
    trackGtmEvent({
      name: 'hasStartedSearching',
      commonAnalyticsData,
    });
  },

  hasOpenedSearchComponent() {
    trackGtmEvent({
      name: 'hasOpenedSearchComponent',
      commonAnalyticsData,
    });
  },

  hasExitedSearchComponent({searchString, countResultsShown}: {searchString: string; countResultsShown: number}) {
    trackGtmEvent({
      name: 'hasExitedSearchComponent',
      eventData: {
        searchString,
        countResultsShown,
      },
      commonAnalyticsData,
    });
  },

  hasClickedSearchResult({
    searchString,
    countResultsShown,
    selectedItemType,
    selectedItemName,
    selectedItemID,
    selectedItemPositionResultsList,
    selectedItemPositionTypeList,
    searchListValue,
  }: EventClickedSearchResultProps) {
    trackGtmEvent({
      name: 'hasClickedSearchResult',
      eventData: {
        productSubPosition: selectedItemPositionTypeList,
        searchString,
        countResultsShown,
        searchListValue,
      },
      gaModifiedData: {
        selectedItemID,
        selectedItemName,
        selectedItemType,
        selectedItemPositionResultsList,
        productSubPosition: selectedItemPositionTypeList,
        searchString,
        countResultsShown,
        searchListValue,
      },
      commonAnalyticsData,
    });
  },

  hasOpenedReorderDropdown() {
    trackGtmEvent({
      name: 'hasOpenedReorderDropdown',
      commonAnalyticsData,
    });
  },

  hasClickedReorder(eventData: EventDataType) {
    trackGtmEvent({
      name: 'hasClickedReorder',
      eventData,
      commonAnalyticsData,
    });
  },

  hasReorderPopupUnavailable(eventData: EventDataType) {
    trackGtmEvent({
      name: 'hasReorderPopupUnavailable',
      eventData,
      commonAnalyticsData,
    });
  },

  hasViewedCart(eventData: EventDataType) {
    trackGtmEvent({
      name: 'view_cart',
      eventData,
      commonAnalyticsData,
    });
  },

  hasReorderPopupAllUnavailable() {
    trackGtmEvent({
      name: 'hasReorderPopupAllUnavailable',
      commonAnalyticsData,
    });
  },

  hasClickedViewOrder(eventData: EventDataType) {
    trackGtmEvent({
      name: 'hasClickedViewOrder',
      eventData,
      commonAnalyticsData,
    });
  },

  enterFeedback_click(eventData: EventDataType) {
    trackGtmEvent({
      name: 'enterFeedback_click',
      eventData,
      commonAnalyticsData,
    });
  },

  enterFeedback_submit(eventData: EventDataType) {
    trackGtmEvent({
      name: 'enterFeedback_submit',
      eventData,
      commonAnalyticsData,
    });
  },

  hasClickedDeleteAccount() {
    trackGtmEvent({
      name: 'hasClickedDeleteAccount',
      commonAnalyticsData,
    });
  },

  hasContinueDeleteAccount() {
    trackGtmEvent({
      name: 'hasContinueDeleteAccount',
      commonAnalyticsData,
    });
  },

  hasApproveDeleteAccount() {
    trackGtmEvent({
      name: 'hasApproveDeleteAccount',
      commonAnalyticsData,
    });
  },

  hasSuccessfullyDeleteAccount(eventData: EventDeleteAccountProps) {
    trackGtmEvent({
      name: 'hasSuccessfullyDeleteAccount',
      eventData,
      commonAnalyticsData,
    });
  },

  hasFailedDeleteAccount(eventData: EventDeleteAccountProps) {
    trackGtmEvent({
      name: 'hasFailedDeleteAccount',
      eventData,
      commonAnalyticsData,
    });
  },

  hasViewedProductDetail(dishData: CurrentProduct) {
    trackGtmEvent({
      name: 'hasViewedProductDetail',
      gaModifiedData: {
        event: GoogleAnalyticsEvents.VIEW_ITEM,
        ecommerce: {
          items: getCurrentProductsForGA([dishData]),
        },
      },
      commonAnalyticsData,
    });
  },

  hasAddedToCart(dishData: CurrentProduct) {
    trackGtmEvent({
      name: 'hasAddedToCart',
      gaModifiedData: {
        event: GoogleAnalyticsEvents.ADD_TO_CART,
        ecommerce: {
          items: getCurrentProductsForGA([dishData]),
        },
      },
      commonAnalyticsData,
    });
  },

  hasRemovedFromCart(dishData: CurrentProduct) {
    trackGtmEvent({
      name: 'hasRemovedFromCart',
      gaModifiedData: {
        event: GoogleAnalyticsEvents.REMOVE_FROM_CART,
        ecommerce: {
          items: getCurrentProductsForGA([dishData]),
        },
      },
      commonAnalyticsData,
    });
  },

  hasViewedMinimumOrderPopup() {
    trackGtmEvent({
      name: 'hasViewedMinimumOrderPopup',
      commonAnalyticsData,
    });
  },

  hasReachedMOV() {
    trackGtmEvent({
      name: 'hasReachedMOV',
      commonAnalyticsData,
    });
  },

  hasResetShoopingCart() {
    trackGtmEvent({
      name: 'hasResetShoopingCart',
      commonAnalyticsData,
    });
  },

  hasBeginCheckout(currentProducts: CurrentProduct[]) {
    trackGtmEvent({
      name: 'hasBeginCheckout',
      gaModifiedData: {
        event: GoogleAnalyticsEvents.BEGIN_CHECKOUT,
        ecommerce: {
          items: getCurrentProductsForGA(currentProducts),
        },
      },
      commonAnalyticsData,
    });
  },

  hasCancelledOrder(eventData: EventDataType) {
    trackGtmEvent({
      name: 'hasCancelledOrder',
      eventData,
      commonAnalyticsData,
    });
  },

  hasSelectedChain(eventData: EventDataType) {
    trackGtmEvent({
      name: 'hasSelectedChain',
      eventData,
      commonAnalyticsData,
    });
  },

  hasViewedAlcoholPopUp() {
    trackGtmEvent({
      name: 'hasViewedAlcoholPopUp',
      commonAnalyticsData,
    });
  },

  firstBisPopupView() {
    trackGtmEvent({
      name: 'firstBisPopup_view',
      commonAnalyticsData,
    });
  },

  firstBisPopupClick(eventData: EventDataType) {
    trackGtmEvent({
      name: 'firstBisPopup_click',
      eventData,
      commonAnalyticsData,
    });
  },

  hasClickedAlcoholPopUp(eventData: EventDataType) {
    trackGtmEvent({
      name: 'hasClickedAlcoholPopUp',
      eventData,
      commonAnalyticsData,
    });
  },

  hasChangedDateInBillingReport(eventData: {dateSelected: string}) {
    trackGtmEvent({
      name: 'hasChangedDateInBillingReport',
      eventData,
      commonAnalyticsData,
    });
  },
  hasClickedAddressDropdown() {
    trackGtmEvent({
      name: 'hasClickedAddressDropdown',
      commonAnalyticsData,
    });
  },

  hasChangedLocation({addressType}: {addressType: string}) {
    trackGtmEvent({
      name: 'hasChangedLocation',
      eventData: {
        addressType,
      },
      commonAnalyticsData,
    });
  },

  addressStartSearch({searchOption, addressBarType}: AddressStartSearchProps) {
    trackGtmEvent({
      name: 'address_startSearch',
      eventData: {
        searchOption,
        addressBar_type: addressBarType,
      },
      commonAnalyticsData,
    });
  },

  hasModifiedAddress({modifiedAddressType, modifiedLocation}: ModifiedAddressProps) {
    trackGtmEvent({
      name: 'hasModifiedAddress',
      eventData: {
        modifiedAddressType,
        modifiedLocation,
      },
      commonAnalyticsData,
    });
  },

  hasSwitchedDeliveryType({deliveryMethod}: {deliveryMethod: string}) {
    trackGtmEvent({
      name: 'hasSwitchedDeliveryType',
      eventData: {
        deliveryMethod,
      },
      commonAnalyticsData,
    });
  },

  hasRecivedActiveOrderWidget(eventData: ActiveOrderRecivedProps) {
    trackGtmEvent({
      name: 'hasRecivedActiveOrderWidget',
      eventData,
      commonAnalyticsData,
    });
  },

  hasRecivedCancelledOrderWidget(eventData: ActiveOrderRecivedProps) {
    trackGtmEvent({
      name: 'hasRecivedCancelledOrderWidget',
      eventData,
      commonAnalyticsData,
    });
  },

  hasClickedOrderWidget(eventData: ActiveOrderOnClickProps) {
    trackGtmEvent({
      name: 'hasClickedOrderWidget',
      eventData,
      commonAnalyticsData,
    });
  },

  hasCheckedOutDiscountCampus({dinningRoomNoPackingRequired}: {dinningRoomNoPackingRequired: boolean}) {
    trackGtmEvent({
      name: 'hasCheckedOutDiscountCampus',
      eventData: {
        discountCheckedOption: dinningRoomNoPackingRequired ? 'sit' : 'takeaway',
      },
      commonAnalyticsData,
    });
  },

  hasStartedMenuSearch() {
    trackGtmEvent({
      name: 'hasStartedMenuSearch',
      commonAnalyticsData,
    });
  },

  hasDeletedMenuSearch() {
    trackGtmEvent({
      name: 'hasDeletedMenuSearch',
      commonAnalyticsData,
    });
  },

  hasFilteredKitchenType(eventData: EventDataType) {
    trackGtmEvent({
      name: 'hasFilteredKitchenType',
      eventData,
      commonAnalyticsData,
    });
  },

  hasUnFilteredKitchenType(eventData: EventDataType) {
    trackGtmEvent({
      name: 'hasUnFilteredKitchenType',
      eventData,
      commonAnalyticsData,
    });
  },

  hasViewedOfficeSupplyFilter() {
    trackGtmEvent({
      name: 'hasViewedOfficeSupplyFilter',
      commonAnalyticsData,
    });
  },

  hasOpenedOfficeSupplyFilter() {
    trackGtmEvent({
      name: 'hasOpenedOfficeSupplyFilter',
      commonAnalyticsData,
    });
  },

  hasClickedOfficeSupplyFilter(eventData: EventHasClickedOfficeSupplyFilterProps) {
    trackGtmEvent({
      name: 'hasClickedOfficeSupplyFilter',
      eventData,
      commonAnalyticsData,
    });
  },

  hasResetOfficeSupplyFilter() {
    trackGtmEvent({
      name: 'hasResetOfficeSupplyFilter',
      commonAnalyticsData,
    });
  },

  hasFutureOrderDishUnavailable(eventData: EventDataType) {
    trackGtmEvent({
      name: 'hasFutureOrderDishUnavailable',
      eventData,
      commonAnalyticsData,
    });
  },

  hasOpenAccessibilityMenu() {
    trackGtmEvent({
      name: 'hasOpenAccessibilityMenu',
      commonAnalyticsData,
    });
  },

  hasClosedAccessibilityMenu() {
    trackGtmEvent({
      name: 'hasClosedAccessibilityMenu',
      commonAnalyticsData,
    });
  },

  downloadApp_view() {
    trackGtmEvent({
      name: 'downloadApp_view',
    });
  },

  downloadApp_click(eventData: EventDataType = {}) {
    trackGtmEvent({
      name: 'downloadApp_click',
      eventData,
    });
  },

  hasClickedAccessibilityMenu(eventData: EventDataType) {
    trackGtmEvent({
      name: 'hasClickedAccessibilityMenu',
      eventData,
      commonAnalyticsData,
    });
  },

  formErrorMessageView({errorType, formName, onCheckout, authenticationProvider}: FormErrorMessageProps) {
    trackGtmEvent({
      name: 'formErrorMessage_view',
      eventData: {
        errorType,
        formName,
        onCheckout: onCheckout ? 1 : 0,
        authentication_provider: authenticationProvider,
      },
      commonAnalyticsData,
    });
  },

  hasViewedCarousel(eventData: ViewedCarouselProps) {
    trackGtmEvent({
      name: 'hasViewedCarousel',
      eventData,
      commonAnalyticsData,
    });
  },

  hasClickedShowAllCarousel(eventData: HasClickedShowAllCarouselProps) {
    trackGtmEvent({
      name: 'hasClickedShowAllCarousel',
      eventData,
      commonAnalyticsData,
    });
  },

  hasClickedCarousel(eventData: ViewedCarouselProps) {
    trackGtmEvent({
      name: 'hasClickedCarousel',
      eventData,
      commonAnalyticsData,
    });
  },

  hasSwipeCarousel(eventData: ViewedCarouselProps) {
    trackGtmEvent({
      name: 'hasSwipeCarousel',
      eventData,
      commonAnalyticsData,
    });
  },

  abTestParticipated({experimentId, variant}: {experimentId: string; variant: string}) {
    trackGtmEvent({
      name: 'abTestParticipated',
      eventData: {
        experimentId,
        variant,
      },
      commonAnalyticsData,
    });
  },

  nlxOrderType_click(eventData: {original_orderType: string; pageSource: string}) {
    trackGtmEvent({
      name: 'nlxOrderType_click',
      eventData,
      commonAnalyticsData,
    });
  },

  nlxOrderType_select(eventData: NlxOrderTypeSelectProps) {
    trackGtmEvent({
      name: 'nlxOrderType_select',
      eventData,
      commonAnalyticsData,
    });
  },

  // #region 10biscredit
  handleTenBisCredit({type, eventData, errorType}: {type: TenBisCreditEventTypes; eventData?: TenBisCreditEventData; errorType?: string}) {
    const eventName = `transferBalance_${type}`;

    const voidEvents = [
      TenBisCreditEventTypes.CLICK,
      TenBisCreditEventTypes.READ_MORE,
    ];

    if (voidEvents.includes(type)) {
      trackGtmEvent({
        name: eventName,
        commonAnalyticsData,
      });
      return;
    }

    trackGtmEvent({
      name: eventName,
      eventData: {
        available_balance: eventData?.availableBalance,
        transfer_balance: eventData?.transferBalance,
        errorType,
      },
      commonAnalyticsData,
    });
  },
  // #endregion

  handleCloseRestaurantModal({eventName, resId, clickOptions}: {eventName: GeneralErrorModalEventNames; resId: string; clickOptions?: GeneralErrorModalClickOptions}) {
    const eventData: Record<string, string | number> = {
      restaurantID: Number(resId),
    };

    if (clickOptions) {
      eventData.click_options = clickOptions;
    }

    trackGtmEvent({
      name: eventName,
      eventData,
      commonAnalyticsData,
    });

  },

  handleOnlyOpenForModal({eventName, resId, orderType, clickOptions}: {eventName: OnlyOpenForModalEventNames; resId: string; orderType: 'pickup' | 'delivery'; clickOptions?: OnlyOpenForModalClickOptions}) {
    const eventData: Record<string, string | number> = {
      restaurantID: Number(resId),
      order_type: orderType,
      restaurantOpeningStatus: 'Open',
    };

    if (clickOptions) {
      eventData.click_options = clickOptions;
    }

    trackGtmEvent({
      name: eventName,
      eventData,
      commonAnalyticsData,
    });
  },

  categoryRibbon_click(
    {id, position, type}:
    {
      id: Cuisine['id'] | typeof STORE_BY_BUSINESS_TYPE | string;
      position: number;
      type: 'cuisine' | 'preference';
    },
  ) {
    trackGtmEvent({
      name: 'categoryRibbon_click',
      eventData: {
        category_ribbon_name: id,
        category_ribbon_position: position,
        category_ribbon_type: type === 'cuisine' ? 'kitchenType' : 'preference',
      },
      commonAnalyticsData,
    });
  },

  menuCategory_click({menuCategoryName}: {menuCategoryName: string}) {
    trackGtmEvent({
      name: 'menuCategory_click',
      eventData: {
        menuCategoryName,
      },
    });
  },

  topMenuCategory_click({menuCategoryName}: {menuCategoryName: string}) {
    trackGtmEvent({
      name: 'topMenuCategory_click',
      eventData: {menuCategoryName},
    });
  },
  
  multipassFetchFailed({orderId}: {orderId: OrderData['orderId']}) {
    trackGtmEvent({
      name: 'multiPassFailedLoad_view',
      eventData: {orderId},
      commonAnalyticsData,
    });
  },

  multipassFetchRetryClick({orderId}: {orderId: OrderData['orderId']}) {
    trackGtmEvent({
      name: 'multiPassFailedLoad_tryAgainClick',
      eventData: {orderId},
      commonAnalyticsData,
    });
  },

  resDiscount_click({resDiscountId}: {resDiscountId: OrderData['discountCoupon']['id']}) {
    trackGtmEvent({
      name: 'resDiscount_click',
      eventData: {resDiscountId},
      commonAnalyticsData,
    });
  },

  resDiscount_checkoutChange({resDiscountId, newCouponId}: {resDiscountId: OrderData['discountCoupon']['id'] | null; newCouponId: OrderData['discountCoupon']['id']}) {
    trackGtmEvent({
      name: 'resDiscount_checkoutChange',
      eventData: {resDiscountId, newCouponId},
      commonAnalyticsData,
    });
  },

  coupon_submit({couponCode}: {
    couponCode: string | null;
  }) {
    trackGtmEvent({
      name: 'coupon_submit',
      eventData: {
        couponCode,
        // currently the event is fired only in checkout, so there is no other option for screenSource
        // but should be appended anyway
        screenSource: 'checkout',
      },
      commonAnalyticsData,
    });
  },

  coupon_submitFailed({couponCode, couponError}: {
    couponCode: string | null;
    couponError: string;
  }) {
    trackGtmEvent({
      name: 'coupon_submitFailed',
      eventData: {
        couponCode,
        error_message: couponError,
        // currently the event is fired only in checkout, so there is no other option for screenSource
        // but should be appended anyway
        screenSource: 'checkout',
      },
      commonAnalyticsData,
    });
  },

  coupon_submitSuccess({couponCode}: {
    couponCode: string | null;
  }) {
    trackGtmEvent({
      name: 'coupon_submitSuccess',
      eventData: {
        couponCode,
        // currently the event is fired only in checkout, so there is no other option for screenSource
        // but should be appended anyway
        screenSource: 'checkout',
      },
      commonAnalyticsData,
    });
  },

  couponButton_click({btnText}: {
    btnText: string;
  }) {
    trackGtmEvent({
      name: 'couponButton_click',
      eventData: {
        text_link: btnText,
        // currently the event is fired only in checkout, so there is no other option for screenSource
        // but should be appended anyway
        screenSource: 'checkout',
      },
      commonAnalyticsData,
    });
  },

  coupon_remove({couponCode}: {
    couponCode: string;
  }) {
    trackGtmEvent({
      name: 'coupon_remove',
      eventData: {
        couponCode,
        // currently the event is fired only in checkout, so there is no other option for screenSource
        // but should be appended anyway
        screenSource: 'checkout',
      },
      commonAnalyticsData,
    });
  },

  moments_view() {
    trackGtmEvent({
      name: '10bisMoments_view',
    });
  },

  moments_click() {
    trackGtmEvent({
      name: '10bisMoments_click',
    });
  },

  filterBox_click() {
    trackGtmEvent({
      name: 'filterBox_click',
    });
  },

  filter_reset() {
    trackGtmEvent({
      name: 'filter_reset',
    });
  },

  foodTracker_view({referralDevice, orderStatus, displayTime}: TrackerSharedAnalyticsProps) {
    trackGtmEvent({
      name: 'foodTracker_view',
      eventData: {
        referral_device: referralDevice,
        order_status: orderStatus,
        display_time: displayTime,
      },
    });
  },
  foodTracker_statusChange({referralDevice, orderStatus, displayTime}: TrackerSharedAnalyticsProps) {
    trackGtmEvent({
      name: 'foodTracker_statusChange',
      eventData: {
        referral_device: referralDevice,
        order_status: orderStatus,
        display_time: displayTime,
      },
    });
  },
  foodTracker_errorMessage({referralDevice, orderStatus, displayTime, errorMessage}: TrackerSharedAnalyticsProps & {errorMessage: string}) {
    trackGtmEvent({
      name: 'foodTracker_errorMessage',
      eventData: {
        referral_device: referralDevice,
        order_status: orderStatus,
        display_time: displayTime,
        error_message: errorMessage,
      },
    });
  },

  autoCreditToggle({toggleOption}: AutoCreditToggleProps) {
    trackGtmEvent({
      name: 'autoCredit_toggle',
      eventData: {
        toggle_option: toggleOption ? 1 : 0,
      },
    });
  },

  autoCreditToggleFailed({toggleOption, errorMessage, apiError}: AutoCreditToggleFailedProps) {
    trackGtmEvent({
      name: 'autoCredit_toggleFailed',
      eventData: {
        toggle_option: toggleOption ? 1 : 0,
        error_message: errorMessage,
        api_error: apiError,
      },
    });
  },

  payWithApple_click(eventData: EventDataType) {
    trackGtmEvent({
      name: 'payWithApple_click',
      eventData,
    });
  },

  personalArea_click() {
    trackGtmEvent({
      name: 'personalArea_click',
    });
  },

  myOrdersArea_click() {
    trackGtmEvent({
      name: 'myOrdersArea_click',
    });
  },

  paymentMethods_click() {
    trackGtmEvent({
      name: 'paymentMethods_click',
    });
  },

  myBenefits_click() {
    trackGtmEvent({
      name: 'myBenefits_click',
    });
  },

  settings_click() {
    trackGtmEvent({
      name: 'settings_click',
    });
  },

  settingsOption_change({settingControllerName, settingsAction}: SettingsOptionChangeProps) {
    trackGtmEvent({
      name: 'settingsOption_change',
      eventData: {
        settings_option: settingsOptionAnalyticsNameMap[settingControllerName],
        settings_action: settingsAction,
      },
    });
  },
  addPaymentMethod_click(eventData: EventDataType) {
    trackGtmEvent({
      name: 'addPaymentMethod_click',
      commonAnalyticsData,
      eventData,
    });
  },
  addPaymentMethod_successful(eventData: EventDataType) {
    trackGtmEvent({
      name: 'addPaymentMethod_successful',
      commonAnalyticsData,
      eventData,
    });
  },
  addPaymentMethod_failed(eventData: EventDataType) {
    trackGtmEvent({
      name: 'addPaymentMethod_failed',
      commonAnalyticsData,
      eventData,
    });
  },
  removePaymentMethod_click(eventData: EventDataType) {
    trackGtmEvent({
      name: 'removePaymentMethod_click',
      commonAnalyticsData,
      eventData,
    });
  },
  removePaymentMethod_successful(eventData: EventDataType) {
    trackGtmEvent({
      name: 'removePaymentMethod_successful',
      commonAnalyticsData,
      eventData,
    });
  },

  addressErrorMessageClick({errorType, formName, onCheckout}: FormErrorMessageProps) {
    trackGtmEvent({
      name: 'addressErrorMessage_click',
      eventData: {
        errorType,
        formName,
        onCheckout: onCheckout ? 1 : 0,
      },
      commonAnalyticsData,
    });
  },
};

export function trackEvent(eventName: keyof typeof eventHandlers, payload?: any) {
  const handler = eventHandlers[eventName];

  if (!handler) {
    logger.warn(`a handler was not found for the event ${eventName}`);
  }

  try {
    handler(payload);
    logger.verbose(`${eventName} event handled.`);
  } catch (err) {
    logger.warn('analytics event tracking error', {err});
  }
}

export const trackPageview = ({
  eventPayload = {},
  currentLanguageKey,
  pageGroup,
}: TrackPageviewProps) => {
  if (!pageGroup || !currentLanguageKey) {
    logger.error('trackPageview received wrong data', {eventPayload, currentLanguageKey, pageGroup});
    return;
  }

  const {internallyRefreshedOnLocation} = getCurrentLocation();
  if (internallyRefreshedOnLocation) {
    // if the page was refreshed internally (like from the refresh modal) we don't want to track the page again
    return;
  }

  const gaPageViewDataObj = {
    event: GoogleAnalyticsEvents.PAGE_VIEW,
    url: document.location.href,
    languageKey: currentLanguageKey,
    pageGroup,
    isLoggedIn: !!commonAnalyticsData?.userData,
    user_data: {
      companyId: commonAnalyticsData?.userData?.companyId,
      crossPlatformCustomerId: commonAnalyticsData?.userData?.crossPlatformCustomerId,
      userCity: commonAnalyticsData?.currentAddressCityName,
      LoggedInType: commonAnalyticsData?.userType || 'none',
    },
    utag_data: {
      tealium_event: 'custom_dev_none',
    },
  };

  const gaMenuViewDataObj = eventPayload?.restaurantData ? {
    event: GoogleAnalyticsEvents.MENU_VIEW,
    ...eventPayload.restaurantData,
    utag_data: {
      tealium_event: 'custom_dev_none',
    },
  } : null;

  const gaPurchaseDataObj = (eventPayload?.transactionData && eventPayload.currentProducts && pageGroup !== 'Reload_Confirmation') ? {
    event: GoogleAnalyticsEvents.PURCHASE,
    ecommerce: {
      ...eventPayload.transactionData,
      items: getCurrentProductsForGA(eventPayload.currentProducts),
    },
    utag_data: {
      tealium_event: 'custom_dev_none',
    },
  } : null;

  // GA events submission
  window.dataLayer.push(gaPageViewDataObj);

  if (gaMenuViewDataObj) {
    window.dataLayer.push(gaMenuViewDataObj);
  }

  if (gaPurchaseDataObj) {
    window.dataLayer.push(gaPurchaseDataObj);
  }
};

// Used in App.js on application mount
export const collectWebVitalsMetrics = () => {
  const reportVital = (metric: Metric) => {
    trackGtmEvent({
      name: 'web_vitals',
      eventData: {
        vital_name: metric.name,
        vital_value: metric.value,
        vital_delta: metric.delta,
      },
      commonAnalyticsData,
    });
  };

  onTTFB(reportVital);
  onLCP(reportVital);
  onFID(reportVital);
  onCLS(reportVital);
  onFCP(reportVital);
};
